// Navigation model for the dashboard shell.
// Every label is an i18n key (never a hardcoded string — CLAUDE.md rule 6).
// `phase` drives the "coming in P<n>" empty state for not-yet-built modules.
// `locked` marks premium/addon modules (mirrors WhatChimp's locked-icon pattern).
import type { ComponentType } from 'react';
import type { IconProps } from '../components/icons/types';
import {
  OnboardingIcon,
  DashboardIcon,
  ConnectIcon,
  ProfileIcon,
  BotIcon,
  SubscribersIcon,
  BroadcastingIcon,
  InboxIcon,
  CatalogIcon,
  FormsIcon,
  PaymentsIcon,
  AppointmentIcon,
  AdsIcon,
  IntegrationsIcon,
  SettingsIcon,
  PermissionsIcon,
  BillingIcon,
  UserIcon,
  GoogleIcon,
  FlowsIcon,
  ExternalLinkIcon,
  HelpCircleIcon,
} from '../components/icons';

// WP-00: icon is now a component (not an emoji); color provides per-item accent tint
export interface NavItem {
  /** i18n key for the label. */
  key: string;
  /** Absolute route path. */
  path: string;
  /** SVG icon component (WhatChimp design parity). */
  icon: ComponentType<IconProps>;
  /** Accent color class for the icon. */
  color?: string;
  /** Build phase this module ships in (PRD §10). Omitted = already live. */
  phase?: number;
  /** Premium/addon module — shows a "Soon" badge. */
  locked?: boolean;
}

// Order mirrors PRD §4 navigation list exactly.
// WP-00: WhatChimp color-coded icon set
export const NAV_ITEMS: NavItem[] = [
  { key: 'nav.onboarding', path: '/onboarding', icon: OnboardingIcon, color: 'text-violet-500', phase: 1 },
  { key: 'nav.dashboard', path: '/', icon: DashboardIcon, color: 'text-indigo-600' },
  { key: 'nav.connect', path: '/connect', icon: ConnectIcon, color: 'text-emerald-500', phase: 2 },
  { key: 'nav.waProfile', path: '/whatsapp-profile', icon: ProfileIcon, color: 'text-sky-500' },
  { key: 'nav.bots', path: '/bots', icon: BotIcon, color: 'text-violet-600', phase: 6 },
  // WP-08: Visual Flow Builder
  { key: 'nav.flows', path: '/flows', icon: FlowsIcon, color: 'text-purple-600' },
  { key: 'nav.subscribers', path: '/subscribers', icon: SubscribersIcon, color: 'text-sky-500', phase: 4 },
  { key: 'nav.broadcasting', path: '/broadcasting', icon: BroadcastingIcon, color: 'text-rose-500', phase: 5 },
  { key: 'nav.inbox', path: '/inbox', icon: InboxIcon, color: 'text-emerald-600', phase: 3 },
  { key: 'nav.catalog', path: '/catalog', icon: CatalogIcon, color: 'text-amber-500' },
  // WP-12: Store Automation
  { key: 'nav.storeAutomation', path: '/store', icon: CatalogIcon, color: 'text-amber-600' },
  { key: 'nav.forms', path: '/forms', icon: FormsIcon, color: 'text-blue-500' },
  { key: 'nav.payments', path: '/payments', icon: PaymentsIcon, color: 'text-emerald-600', phase: 11, locked: true },
  { key: 'nav.appointment', path: '/appointment', icon: AppointmentIcon, color: 'text-teal-500' },
  { key: 'nav.ads', path: '/ads', icon: AdsIcon, color: 'text-violet-500' },
  { key: 'nav.integrations', path: '/integrations', icon: IntegrationsIcon, color: 'text-cyan-600', phase: 9 },
  // WP-05: Google integration
  { key: 'nav.googleIntegration', path: '/google-integration', icon: GoogleIcon, color: 'text-amber-500' },
  // WP-13: HTTP API manager
  { key: 'nav.httpApi', path: '/http-api', icon: IntegrationsIcon, color: 'text-cyan-500' },
  // WP-14: Webhook workflows
  { key: 'nav.webhookWorkflows', path: '/webhook-workflows', icon: IntegrationsIcon, color: 'text-violet-600' },
  { key: 'nav.settings', path: '/settings', icon: SettingsIcon, color: 'text-slate-500', phase: 2 },
  { key: 'nav.permissions', path: '/permissions', icon: PermissionsIcon, color: 'text-amber-600', phase: 1 },
  // WP-15: User manager
  { key: 'nav.userManager', path: '/user-manager', icon: UserIcon, color: 'text-blue-600' },
  { key: 'nav.billing', path: '/billing', icon: BillingIcon, color: 'text-emerald-600', phase: 7 },
];

export interface TabItem {
  key: string;
  path: string;
  icon: ComponentType<IconProps>;
}

// Mobile bottom tab bar (PRD §6.2): Dashboard, Inbox, Broadcast, Contacts, Bots.
export const TAB_ITEMS: TabItem[] = [
  { key: 'tab.dashboard', path: '/', icon: DashboardIcon },
  { key: 'tab.inbox', path: '/inbox', icon: InboxIcon },
  { key: 'tab.broadcast', path: '/broadcasting', icon: BroadcastingIcon },
  { key: 'tab.contacts', path: '/subscribers', icon: SubscribersIcon },
  { key: 'tab.bots', path: '/bots', icon: BotIcon },
];

export interface HelpItem {
  key: string;
  icon: ComponentType<IconProps>;
  /** In-app route to navigate to. */
  path?: string;
  /** External link (opens in a new tab). */
  href?: string;
}

// Topbar Help menu (PRD §4.17). Support items open the public marketing site
// (watag.tagnologys.com); invoices stay in-app (account-private).
const SITE = 'https://watag.tagnologys.com';
export const HELP_ITEMS: HelpItem[] = [
  { key: 'help.videos', icon: ExternalLinkIcon, href: `${SITE}/learn.html` },
  { key: 'help.onboarding', icon: OnboardingIcon, href: `${SITE}/help.html` },
  { key: 'help.docs', icon: FormsIcon, href: `${SITE}/docs.html` },
  { key: 'help.whatsapp', icon: ConnectIcon, href: 'https://wa.me/96876912321' },
  { key: 'help.ticket', icon: HelpCircleIcon, path: '/support' },
  { key: 'help.invoices', icon: BillingIcon, path: '/billing' },
];

